import { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";

export function TestStartHandler() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    const fetchTestAndRedirect = async () => {
      if (!token) {
        toast({
          title: "Invalid Link",
          description: "This test link is invalid. Please check the URL and try again.",
          variant: "destructive",
        });
        navigate("/");
        return;
      }

      try {
        // Query the tests table using the share token
        const { data: test, error } = await supabase
          .from("tests")
          .select("id, title, is_active, status")
          .eq("share_token", token)
          .single();

        if (error || !test) {
          console.error("Error fetching test:", error);
          toast({
            title: "Test Not Found",
            description: "The test you're looking for doesn't exist or has been removed.",
            variant: "destructive",
          });
          navigate("/");
          return;
        }

        // Check if test is active and published
        if (!test.is_active || test.status !== "published") {
          toast({
            title: "Test Unavailable",
            description: "This test is currently not available.",
            variant: "destructive",
          });
          navigate("/");
          return;
        }

        // Redirect to the test taking page with the test ID
        navigate(`/test/${test.id}/take`);
      } catch (error) {
        console.error("Unexpected error:", error);
        toast({
          title: "Error",
          description: "An unexpected error occurred. Please try again.",
          variant: "destructive",
        });
        navigate("/");
      }
    };

    fetchTestAndRedirect();
  }, [token, navigate, toast]);

  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="flex flex-col items-center gap-4">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-muted-foreground">Loading test...</p>
      </div>
    </div>
  );
}
